
import Vue from 'vue';
import {
  IngestDocument, IngestFragment, IngestFragmentBox, IngestTextFragment, Label, Suggestion,
} from '@/model';
import { distinct, groupBy, notEmpty } from '@/util/array-util';
import { merge } from '@/util/text-selection';
import labels from '@/store/labels';
import LabelPanel from './LabelPanel.vue';

export default Vue.extend({
  components: { LabelPanel },
  name: 'IngestStats',

  props: {
    ingestion: { type: Object as () => IngestDocument, required: true },
    fragments: { type: Array as () => IngestFragment[], default: () => [] },
    suggestions: { type: Array as () => Suggestion[] | undefined, required: false },
  },

  data() {
    return {
      filterType: undefined as string | undefined,
    };
  },

  computed: {
    labelTypes() {
      return distinct(labels.state.labels.map((x) => x.type)).filter(notEmpty).sort();
    },

    numberOfChars(): number {
      return this.ingestion.pages
        .flatMap((page) => page.boxes)
        .map((box) => box.text)
        .filter(notEmpty)
        .map((text) => text.length)
        .reduce((acc, c) => acc + c, 0);
    },

    numberOfWords(): number {
      return this.ingestion.pages
        .flatMap((page) => page.boxes)
        .map((box) => box.text)
        .filter(notEmpty)
        .map((text) => text.split(/\b\s+(?!$)/).length) // Could be improved
        .reduce((acc, c) => acc + c, 0);
    },

    numberOfPages(): number {
      return this.ingestion.pages.length;
    },

    numberOfBoxes(): number {
      return this.ingestion.pages
        .flatMap((page) => page.boxes.length)
        .reduce((acc, c) => acc + c, 0);
    },

    numberOfFragments(): number {
      return this.fragments.length;
    },

    numberOfSelectedChars(): number {
      return this.mergeBoxes(
        this.textFragments
          .flatMap((textFragment) => textFragment.boxes),
      )
        .reduce((acc, c) => acc + (c.to - c.from), 0);
    },

    textFragments(): IngestTextFragment[] {
      return this.fragments.filter((fragment): fragment is IngestTextFragment => fragment.type === 'text');
    },

    labelFrequency(): { label: Label | undefined; count: number; chars: number}[] {
      return Array.from(groupBy(
        this.textFragments
          .flatMap((fragment) => fragment.labels.map((l) => ({ id: l.labelId, boxes: fragment.boxes }))),
        (l) => l.id,
      )).map(([id, group]) => ({
        label: labels.getters.lookup.get(id),
        count: group.length,
        chars: this.mergeBoxes(group.flatMap((l) => l.boxes)).reduce((acc, c) => acc + (c.to - c.from), 0),
      }))
        .filter((l) => this.filterType === undefined || l.label?.type === this.filterType)
        .sort((a, b) => b.count - a.count);
    },

    uniqueLabels(): Label[] {
      return this.labelFrequency.map((l) => l.label)
        .filter(notEmpty);
    },

    // Suggestion metrics
    totalBoxes(): number {
      return this.allBoxIds.length;
    },

    filteredSuggestions(): Suggestion[] {
      if (!this.suggestions) return [];
      // Only consider suggestions with confidence >= 0.5
      return this.suggestions.filter((s) => (s.confidence ?? 1) >= 0.5);
    },

    suggestionsByBoxId(): Map<number, Suggestion[]> {
      const map = new Map<number, Suggestion[]>();

      for (const suggestion of this.filteredSuggestions) {
        const existing = map.get(suggestion.boxId) || [];
        existing.push(suggestion);
        map.set(suggestion.boxId, existing);
      }
      return map;
    },

    fragmentsByBoxId(): Map<number, IngestFragment[]> {
      const map = new Map<number, IngestFragment[]>();

      for (const fragment of this.textFragments) {
        for (const box of fragment.boxes) {
          const existing = map.get(box.boxId) || [];
          existing.push(fragment);
          map.set(box.boxId, existing);
        }
      }
      return map;
    },

    allBoxIds(): number[] {
      const boxIds = new Set<number>();
      this.ingestion.pages.forEach((page) => {
        page.boxes.forEach((box) => boxIds.add(box.id));
      });
      return Array.from(boxIds);
    },

    // Each box falls into exactly one of TP/FP/FN/TN, based on whether it has
    // a (threshold-filtered) suggestion and/or an actual fragment.
    confusionCounts(): { tp: number; fp: number; fn: number; tn: number } {
      const counts = {
        tp: 0, fp: 0, fn: 0, tn: 0,
      };

      for (const boxId of this.allBoxIds) {
        const hasSuggestion = this.suggestionsByBoxId.has(boxId);
        const hasFragment = this.fragmentsByBoxId.has(boxId);

        if (hasSuggestion && hasFragment) counts.tp++;
        else if (hasSuggestion && !hasFragment) counts.fp++;
        else if (!hasSuggestion && hasFragment) counts.fn++;
        else counts.tn++;
      }

      return counts;
    },

    truePositives(): number {
      return this.confusionCounts.tp;
    },

    falsePositives(): number {
      return this.confusionCounts.fp;
    },

    falseNegatives(): number {
      return this.confusionCounts.fn;
    },

    trueNegatives(): number {
      return this.confusionCounts.tn;
    },

    precision(): number {
      const tp = this.truePositives;
      const fp = this.falsePositives;

      if (tp + fp === 0) return 0;
      return tp / (tp + fp);
    },

    recall(): number {
      const tp = this.truePositives;
      const fn = this.falseNegatives;

      if (tp + fn === 0) return 0;
      return tp / (tp + fn);
    },

    accuracy(): number {
      const tp = this.truePositives;
      const tn = this.trueNegatives;
      const fp = this.falsePositives;
      const fn = this.falseNegatives;

      const total = tp + tn + fp + fn;
      if (total === 0) return 0;
      return (tp + tn) / total;
    },

    f1Score(): number {
      const p = this.precision;
      const r = this.recall;

      if (p + r === 0) return 0;
      return (2 * p * r) / (p + r);
    },

  },

  methods: {
    round(a: number): number {
      return Math.round(a * 100) / 100;
    },
    mergeBoxes(boxes: IngestFragmentBox[]) {
      return Array.from(groupBy(boxes, (b) => b.boxId)).flatMap(([_, group]) => merge<IngestFragmentBox>([...group]));
    },
  },

});
